import React, {FC, useEffect} from "react";
import {string} from "yup";
import {useTranslation} from "@unibuddy/intl";
import {
    Box,
    Button,
    Dialog,
    Form,
    FormGroup,
    Heading,
    RadioButtonFormGroup,
    RadioButtonGroupItem,
    Stack,
    SubmitButton,
    Text,
    TextInput,
} from "@unibuddy/patron";
import {FormikHelpers} from "formik";

import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import {
    ProspectStageLocation,
    ProspectStageNoneOfTheAbove,
    ProspectStagesForUi,
} from "./prospectStages";
import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import {IntlNamespaces} from "../../../../../translations/types";
import {
    INTL_POPUP_SKIP_BUTTON,
    INTL_POPUP_SUBMIT_BUTTON,
    INTL_POPUP_TITLE,
    INTL_POPUP_HEADER,
    INTL_OR,
    INTL_EXPLAIN_HERE,
    INTL_ERROR_REASON_OVER_LIMIT,
    INTL_ERROR_STAGE_NOT_SELECTED,
} from "../../../../../translations/decisionStage";
import {INTL_GENERIC_ERROR} from "../../../../../translations/errorMessage";

import {ErrorMessage} from "../../../Settings/components/ErrorMessage";

type ProspectStageProps = {
    onDismiss: () => void;
    onSubmit: (...args: unknown[]) => void | Promise<void>;
    isOpen: boolean;
    location: ProspectStageLocation;
};

type FormValues = {
    prospectStage: string;
    reason: string;
};

export const ProspectStage: FC<ProspectStageProps> = ({onDismiss, onSubmit, isOpen, location}) => {
    const {t} = useTranslation(IntlNamespaces.DECISION_STAGE);
    const {trackEvent} = useAnalytics();
    const university = useUniversity();

    useEffect(() => {
        trackEvent(sharedEvents.prospectDecisionStagesShown.name, {
            universityId: university.id,
            universityName: university.name,
            where: location,
        });
    }, [university, trackEvent, location]);

    return (
        <Dialog isOpen={isOpen} size="small" verticalAlign="center" bypassScrollLock>
            <Form
                initialValues={{prospectStage: "", reason: "", serverError: ""}}
                validationSchema={{
                    prospectStage: string().required(
                        t(
                            INTL_ERROR_STAGE_NOT_SELECTED.key,
                            INTL_ERROR_STAGE_NOT_SELECTED.defaultValue,
                        ),
                    ),
                    reason: string().max(
                        150,
                        t(
                            INTL_ERROR_REASON_OVER_LIMIT.key,
                            INTL_ERROR_REASON_OVER_LIMIT.defaultValue,
                        ),
                    ),
                }}
                onSubmit={async (
                    values: {prospectStage: string; reason: string},
                    {setSubmitting, setFieldError}: FormikHelpers<FormValues>,
                ) => {
                    setSubmitting(true);
                    try {
                        await onSubmit(values.prospectStage, values.reason);
                    } catch (error) {
                        console.error(error);
                        setFieldError("serverError", error.message as string);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {(props: {errors: {serverError: string; prospectStage: string}}) => {
                    const {errors} = props;

                    return (
                        <Box padding="medium" minH="200px" maxH="90vh" overflow="auto">
                            <Stack space="medium-large">
                                <Stack space="small">
                                    <Box paddingY="xsmall">
                                        <Heading level="1" size="small" align="center">
                                            {t(
                                                INTL_POPUP_HEADER.key,
                                                INTL_POPUP_HEADER.defaultValue,
                                            )}
                                        </Heading>
                                    </Box>
                                    <Box>
                                        <RadioButtonFormGroup
                                            label={t(
                                                INTL_POPUP_TITLE.key,
                                                INTL_POPUP_TITLE.defaultValue,
                                            )}
                                            name="prospectStage"
                                        >
                                            <Stack space="small">
                                                {ProspectStagesForUi.map((stage) => (
                                                    <RadioButtonGroupItem
                                                        value={stage.value}
                                                        key={stage.value}
                                                    >
                                                        {t(stage.translationKey, stage.name)}
                                                    </RadioButtonGroupItem>
                                                ))}
                                                <Box paddingY="small">
                                                    <Text>
                                                        {t(INTL_OR.key, INTL_OR.defaultValue)}
                                                    </Text>
                                                </Box>
                                                <RadioButtonGroupItem
                                                    value={ProspectStageNoneOfTheAbove.value}
                                                >
                                                    {t(
                                                        ProspectStageNoneOfTheAbove.translationKey,
                                                        ProspectStageNoneOfTheAbove.name,
                                                    )}
                                                </RadioButtonGroupItem>
                                            </Stack>
                                            <Box marginLeft="large">
                                                <FormGroup>
                                                    <TextInput
                                                        placeholder={t(
                                                            INTL_EXPLAIN_HERE.key,
                                                            INTL_EXPLAIN_HERE.defaultValue,
                                                        )}
                                                        name="reason"
                                                        characterLimit={150}
                                                    />
                                                </FormGroup>
                                            </Box>
                                        </RadioButtonFormGroup>
                                    </Box>
                                </Stack>
                                {errors.serverError && (
                                    <ErrorMessage
                                        error={t(
                                            `${IntlNamespaces.ERROR_MESSAGE}:${INTL_GENERIC_ERROR.key}`,
                                            INTL_GENERIC_ERROR.defaultValue,
                                        )}
                                    />
                                )}
                                <Box display="flex" flexDirection="row" gap="small">
                                    <Button onClick={onDismiss} block>
                                        {t(
                                            INTL_POPUP_SKIP_BUTTON.key,
                                            INTL_POPUP_SKIP_BUTTON.defaultValue,
                                        )}
                                    </Button>
                                    <SubmitButton color="university" block>
                                        {t(
                                            INTL_POPUP_SUBMIT_BUTTON.key,
                                            INTL_POPUP_SUBMIT_BUTTON.defaultValue,
                                        )}
                                    </SubmitButton>
                                </Box>
                            </Stack>
                        </Box>
                    );
                }}
            </Form>
        </Dialog>
    );
};
